/**
 * apps/command/src/components/ThemeToggle.tsx — dark ⇄ light.
 *
 * ONE CONTROL, IN THE TOP BAR, WITH A WORD BESIDE IT. Not in a settings screen
 * behind three clicks, and not an icon on its own: §15.7's rule against
 * anything that "invites a curious click" cuts both ways — an unlabelled sun in
 * a government toolbar is exactly the control somebody presses to find out what
 * it does, in the middle of an incident.
 *
 * It changes ONE attribute on `<html>` (see `src/ui/theme.ts`); every screen
 * already asks for `bg-panel` and `text-ink-500`, and those resolve per theme
 * through the variables in `src/index.css`. So nothing else re-renders and there
 * is no theme prop threaded anywhere.
 *
 * Dark stays the default and the choice is remembered — the reasoning is in
 * `theme.ts`, and it is about wall displays and a governor's first impression
 * rather than about preference.
 */
import { useState } from 'react';
import { Moon, Sun } from '@phosphor-icons/react';
import { t } from '../i18n/index.js';
import { applyTheme, persistTheme, storedTheme, type Theme } from '../ui/theme.js';

export function ThemeToggle(): React.JSX.Element {
  /**
   * Initialised from storage rather than from a default, so the button's label
   * matches the theme `main.tsx` already applied before the first paint. A
   * `useState('dark')` here would render «Yorugʻ rejim» on a panel that is
   * already light until somebody pressed it twice.
   */
  const [theme, setTheme] = useState<Theme>(() => storedTheme());

  function switchTo(next: Theme): void {
    applyTheme(next);
    persistTheme(next);
    setTheme(next);
  }

  const next: Theme = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      type="button"
      onClick={() => switchTo(next)}
      title={t(next === 'light' ? 'app.themeLight' : 'app.themeDark')}
      className="flex items-center gap-1.5 text-micro text-ink-500 transition-colors duration-state hover:text-ink-300"
    >
      {next === 'light' ? (
        <Sun weight="regular" className="h-4 w-4" aria-hidden="true" focusable="false" />
      ) : (
        <Moon weight="regular" className="h-4 w-4" aria-hidden="true" focusable="false" />
      )}
      <span>{t(next === 'light' ? 'app.themeLight' : 'app.themeDark')}</span>
    </button>
  );
}
